<template>
  <div class="lista-procesos">
    <h3 class="section-title">
      <IconCog class="section-icon" />
      Procesos de Producción
    </h3>
    <el-collapse
      v-model="activeNames"
      accordion
    >
      <el-collapse-item
        v-for="(item, index) in procesosAgrupados"
        :key="index"
        :name="index"
      >
        <template #title>
          <div class="proceso-header">
            <span class="proceso-index">{{ index + 1 }}</span>
            <span class="proceso-nombre">{{ item.titulo }}</span>
            <span
              v-if="item.esImpresion"
              class="proceso-badge"
              >Impresión</span
            >
            <span
              v-if="item.esFueraDeLinea"
              class="proceso-badge proceso-badge-fuera-linea"
              title="Este proceso no está encadenado en línea con el anterior en el mismo recurso"
              >Fuera de línea</span
            >

            <div class="proceso-metricas-header">
              <span
                v-if="!isStale && item.mermaFutura > 0"
                class="proceso-merma-futura"
                title="Merma Futura / Cascada"
              >
                <IconAlertOutline style="font-size: 14px; margin-right: 2px" />
                +{{ formatearUnidad(item.mermaFutura, resolverAbreviaturaUnidadRecurso(item), 2) }}
                (Futura)
              </span>

              <span
                v-if="!isStale && item.tieneMaquina && item.metrosTotales"
                class="proceso-ml"
              >
                {{
                  formatearUnidad(
                    item.metrosTotales,
                    item.unidadTrabajo?.abreviaturaUnidadCantidad,
                    2,
                  )
                }}
              </span>

              <span
                v-if="!isStale && item.horasTotalesLinea > 0"
                class="proceso-horas"
              >
                {{ formatearNumero(item.horasTotalesLinea, 2) }} h
              </span>
            </div>
          </div>
        </template>

        <template v-if="item.tieneMaquina">
          <div class="maquina-info">
            <span class="maquina-label">RECURSO ASIGNADO:</span>
            <span class="maquina-nombre">{{
              item.recursoDescripcion || 'Máquina no especificada'
            }}</span>
          </div>

          <proceso-detalle
            :proceso="item"
            :is-stale="isStale"
          />

          <div
            v-if="item.esGrupo"
            class="sub-procesos-container"
          >
            <el-divider border-style="dashed" />
            <h4 class="sub-procesos-title">Procesos ejecutados en esta línea:</h4>

            <div
              v-for="proc in item.procesos"
              :key="proc.codigoCotizacionElementoProceso"
              class="sub-proceso-item"
            >
              <h5 class="sub-proceso-nombre">
                <span class="punto-indicador"></span>
                {{ proc.descripcion }}

                <span
                  v-if="
                    proc.horasSetupTotal > 0 ||
                    (proc.horasSetupDetalle && proc.horasSetupDetalle.recarga > 0)
                  "
                  class="tiempos-badge"
                >
                  <span style="font-weight: normal; color: #909399; margin-left: 6px">(</span>

                  <small
                    v-if="proc.horasSetupTotal > 0"
                    style="color: #606266; font-weight: normal"
                  >
                    Setup: <strong>{{ formatNumberLocale(proc.horasSetupTotal, 2) }} h</strong>
                  </small>

                  <small
                    v-if="
                      proc.horasSetupTotal > 0 &&
                      proc.horasSetupDetalle &&
                      proc.horasSetupDetalle.recarga > 0
                    "
                    style="color: #c0c4cc; margin: 0 4px"
                    >|</small
                  >

                  <small
                    v-if="proc.horasSetupDetalle && proc.horasSetupDetalle.recarga > 0"
                    style="color: #606266; font-weight: normal"
                  >
                    Recarga:
                    <strong>{{ formatNumberLocale(proc.horasSetupDetalle.recarga, 2) }} h</strong>
                  </small>

                  <span style="font-weight: normal; color: #909399">)</span>
                </span>
              </h5>

              <lista-materiales
                v-if="proc.materiales && proc.materiales.length > 0"
                :materiales="proc.materiales"
                :is-stale="isStale"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <proceso-detalle
            :proceso="item.procesoOriginal"
            :is-stale="isStale"
          />
        </template>
      </el-collapse-item>
    </el-collapse>

    <div
      v-if="!procesosAgrupados.length"
      class="empty-state"
    >
      No hay procesos registrados
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
} from 'vue';
import IconAlertOutline from '~icons/mdi/alert-outline';
import IconCog from '~icons/mdi/cog';
import {
  formatearNumero,
  formatearUnidad,
} from '@/shared/utils/format_produccion.js';
import ProcesoDetalle from './proceso_detalle.vue';
import ListaMateriales from './lista_materiales.vue';
import { formatNumberLocale } from '@/shared/utils/locale_number_utils.js';
import { agruparProcesosPorLinea } from '@/shared/utils/agrupar_procesos_produccion.js';
import { resolverAbreviaturaUnidadRecurso } from '@/shared/utils/unidad_trabajo.js';
const props = defineProps({
  procesos: {
    type: Array,
    default: () => [],
  },
  isStale: {
    type: Boolean,
    default: false,
  },
});

const activeNames = ref(null);

const procesosAgrupados = computed(() => agruparProcesosPorLinea(props.procesos));
</script>

<style scoped>
.lista-procesos {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: #f5f7fa;
  padding: 8px 12px;
  border-radius: 6px;
  border-left: 4px solid #409eff;
}
.section-icon {
  font-size: 18px;
  color: #409eff;
}
.proceso-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding-right: 12px;
}

.proceso-metricas-header {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 12px;
}

.proceso-merma-futura {
  display: flex;
  align-items: center;
  font-family: 'Fira Code', monospace;
  font-size: 11px;
  color: #e6a23c;
  background: #fdf6ec;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #faecd8;
}

.proceso-ml {
  font-family: 'Fira Code', monospace;
  font-size: 12px;
  color: #909399;
  font-weight: 600;
}
.proceso-horas {
  font-family: 'Fira Code', monospace;
  font-size: 12px;
  color: #409eff;
  font-weight: 600;
}
.proceso-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
}
.proceso-nombre {
  font-size: 13px;
  font-weight: 600;
  color: #4b5563;
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.proceso-badge {
  padding: 2px 8px;
  border-radius: 10px;
  background: #ecf5ff;
  color: #409eff;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}
.proceso-badge-fuera-linea {
  background: #fdf6ec;
  color: #e6a23c;
}
.proceso-ml {
  font-family: 'Fira Code', monospace;
  font-size: 12px;
  color: #909399;
  margin-left: auto;
  padding-right: 8px;
}
.empty-state {
  text-align: center;
  color: #909399;
  padding: 24px;
  font-size: 13px;
}

.maquina-info {
  background-color: #f4f4f5;
  border-left: 4px solid #909399;
  padding: 8px 12px;
  margin-bottom: 16px;
  border-radius: 0 4px 4px 0;
  display: flex;
  gap: 8px;
  align-items: center;
}
.maquina-label {
  font-size: 11px;
  font-weight: 700;
  color: #909399;
  text-transform: uppercase;
}
.maquina-nombre {
  font-size: 13px;
  font-weight: 600;
  color: #303133;
}

.sub-procesos-title {
  font-size: 12px;
  font-weight: 700;
  color: #606266;
  text-transform: uppercase;
  margin-bottom: 12px;
}
.sub-proceso-item {
  margin-bottom: 16px;
  padding-left: 8px;
}
.sub-proceso-nombre {
  font-size: 13px;
  font-weight: 700;
  color: #409eff;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}
.punto-indicador {
  width: 6px;
  height: 6px;
  background-color: #409eff;
  border-radius: 50%;
  display: inline-block;
}
.sin-maquina-alert {
  padding: 8px 0;
}
</style>
